import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useRef, useState } from 'react';
import type { ColumnVisibility } from '../../hooks/useColumnPreferences';

interface ColumnSelectorProps {
  columns: ColumnVisibility[];
  onToggle: (field: string) => void;
  onReset: () => void;
}

export const ColumnSelector = ({ columns, onToggle, onReset }: ColumnSelectorProps) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={() => setOpen(true)}
        title="Configure columns"
        sx={{
          color: alpha(theme.palette.text.primary, 0.5),
          '&:hover': { color: theme.palette.primary.main },
        }}
      >
        <Settings sx={{ fontSize: 18 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { borderRadius: '10px', minWidth: 220, p: 1.5, boxShadow: 3 },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            Columns
          </Typography>
          <Button size="small" onClick={onReset} sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
            Reset
          </Button>
        </Box>

        {columns.map((col) => (
          <FormControlLabel
            key={col.field}
            control={
              <Checkbox
                size="small"
                checked={col.visible}
                disabled={col.mandatory}
                onChange={() => onToggle(col.field)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {col.headerName}
              </Typography>
            }
            sx={{ display: 'flex', m: 0, py: 0.25 }}
          />
        ))}
      </Popover>
    </>
  );
};
